import { trans } from "@mongez/localization";
import { HiddenInput } from "@mongez/react-form";
import type { Address } from "core/addresses";
import { SelectInput, TextInput } from "~/design-system/components";
import { Option } from "~/design-system/types";
import { useAddressCityAndDistrict } from "./useAddressCityAndDistrict";
import { getLocalizedValue } from "~/shared/urils/getLocalizedValue";

type SelectAddressCityAndDistrictProps = {
  withDefaultValue?: boolean;
  address?: Address;
  excludeOther?: boolean;
};

const inputClasses = { input: "bg-white/50" };

const selectClasses = { input: "bg-white/50", search: "mt-4" };

function SelectInputSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 w-full rounded-full bg-white/50">
        <div className="flex h-full items-center justify-between px-4">
          <div className="h-4 w-24 rounded bg-gray-300/60"></div>
          <div className="h-4 w-4 rounded bg-gray-300/60"></div>
        </div>
      </div>
    </div>
  );
}

export function SelectAddressCityAndDistrict({
  withDefaultValue = false,
  address,
  excludeOther = false,
}: SelectAddressCityAndDistrictProps) {
  const {
    loading,
    options,
    selectedRegion,
    selectedCity,
    handleRegionChange,
    handleCityChange,
    isOtherCitySelected,
  } = useAddressCityAndDistrict({ address });

  const isLoading = loading.regions || loading.cities;

  const cityOptions = excludeOther
    ? options.cities.filter((c) => c.value !== 0)
    : options.cities;

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      {/* Region Select */}
      <div className="flex-1">
        {loading.regions ? (
          <SelectInputSkeleton />
        ) : (
          <SelectInput
            searchable
            name="region"
            placeholder={getLocalizedValue(selectedRegion?.name) || trans("state")}
            required
            defaultValue={withDefaultValue ? selectedRegion?.id : undefined}
            value={selectedRegion?.id}
            options={options.regions as unknown as Option[]}
            onChange={handleRegionChange}
            classes={selectClasses}
          />
        )}
      </div>

      <div className="flex-1">
        {isLoading ? (
          <SelectInputSkeleton />
        ) : (
          <SelectInput
            searchable
            name="city"
            placeholder={getLocalizedValue(selectedCity?.name) || trans("city")}
            required
            defaultValue={
              withDefaultValue
                ? address?.isOther
                  ? 0
                  : selectedCity?.id
                    ? selectedCity?.id
                    : undefined
                : undefined
            }
            value={selectedCity?.id === 0 ? 0 : selectedCity?.id}
            options={cityOptions as unknown as Option[]}
            onChange={handleCityChange}
            disabled={!selectedRegion || cityOptions.length === 0}
            classes={selectClasses}
          />
        )}
      </div>

      <HiddenInput name="isOther" value={isOtherCitySelected ? true : false} />

      {isOtherCitySelected && (
        <>
          <div className="flex-1">
            <TextInput
              name="otherCity"
              placeholder={trans("city")}
              required
              defaultValue={withDefaultValue ? address?.otherCity : undefined}
              classes={inputClasses}
            />
          </div>
          <div className="flex-1">
            <TextInput
              name="zipCode"
              placeholder={trans("zipCode")}
              required
              defaultValue={
                withDefaultValue && address?.zipCode
                  ? address?.zipCode
                  : undefined
              }
              classes={inputClasses}
            />
          </div>
        </>
      )}

      {!isOtherCitySelected && (
        <div className="flex-1">
          {isLoading || loading.zipCodes ? (
            <SelectInputSkeleton />
          ) : (
            <TextInput
              name="zipCode"
              placeholder={trans("zipCode")}
              defaultValue={
                withDefaultValue && selectedCity ? address?.zipCode : undefined
              }
              required
              disabled={!selectedCity || options.zipCodes.length === 0}
              rules={[
                {
                  validate: ({ value }: any) => {
                    const isValid = options.zipCodes.some(
                      (z) => z.label === value,
                    );
                    if (!isValid) return trans("invalidZipCode");
                  },
                } as any,
              ]}
              classes={inputClasses}
            />
          )}
        </div>
      )}
    </div>
  );
}
